import client from "@/api/openapi-ts";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { usePlugins } from "@/stores/helpers/usePlugins";
import { useErrorStore } from "@/stores/ErrorStore";
import { RankerGeography, Quarter, Ranking } from "@/types/api/CableRanker";
import type { components } from "@/api/schema.d.ts";

type schemas = components["schemas"];

type Filters = {
  meta_audience_id?: number;
  geography?: RankerGeography;
  index_meta_audience_id?: number;
  date_ranges?: {
    start_date: string;
    end_date: string;
  }[];
  day_ranges?: string[];
};

export const useCableRankerStore = defineStore("cableRanker", () => {
  const errorStore = useErrorStore();
  const loading = ref(false);
  const loadingQuarters = ref(false);
  const quarters = ref<Quarter[] | undefined>(undefined);
  const quartersTaskId = ref<string>();
  const filters = ref<Filters>({});
  const currentFilters = ref<Filters>({});
  const rankingsTaskId = ref<string>();
  const rankings = ref<Ranking[]>([]);
  const avgRatings = ref<Ranking[]>([]);
  const avgRatingsByQuarter = ref<object>({});
  const searched = ref(false);
  const resetting = ref(false);

  const { $ws } = usePlugins();

  const setAvgRatings = () => {
    avgRatings.value = Object.values(
      rankings.value.reduce((res: any, item: Ranking) => {
        if (res[item.station]) {
          res[item.station].rating += item.rating;
          res[item.station].impressions += item.impressions;
          res[item.station].count += 1;
        } else {
          res[item.station] = {
            ...item,
            count: 1,
          };
        }
        return res;
      }, {}),
    )
      .map((obj: any) => ({
        ...obj,
        name: obj.station,
        rating: obj.rating / obj.count,
        impressions: obj.impressions / obj.count,
      }))
      .sort((a, b) => {
        return b.rating - a.rating;
      })
      .slice(0, 50);
  };

  const setAvgRatingsByQuarter = () => {
    const quarters = Array.from(
      new Set(
        rankings.value
          .map((r: Ranking) => ({
            quarter: r.quarter,
            year: r.year,
          }))
          .map((o) => JSON.stringify(o)),
      ),
    ).map((o) => JSON.parse(o));
    const callsigns = new Set(rankings.value.map((r) => r.callsign));

    avgRatingsByQuarter.value = quarters.reduce((res: any, q: any) => {
      const qtr = `Q${q.quarter} ${q.year}`;
      res[qtr] = rankings.value
        .filter((r: Ranking) => q.quarter === r.quarter && q.year === r.year)
        .map((r: Ranking) => ({
          ...r,
          name: r.station,
          avgRating: parseFloat(r.rating.toFixed(1)),
          avgIndex: r.index,
          time: qtr,
        }));
      callsigns.forEach((cs) => {
        if (!res[qtr].find((r: Ranking) => r.callsign === cs)) {
          const ranking = rankings.value.find((r) => r.callsign === cs);
          res[qtr].push({
            ...ranking,
            name: ranking?.station,
            quarter: q.quarter,
            year: q.year,
            impressions: 0,
            index: 0,
            index_rating: 0,
            rating: 0,
            avgRating: 0,
            avgIndex: 0,
            time: qtr,
          });
        }
      });
      return res;
    }, {});
  };

  return {
    loading,
    loadingQuarters,
    quarters,
    quartersTaskId,
    filters,
    currentFilters,
    rankingsTaskId,
    rankings,
    avgRatings,
    avgRatingsByQuarter,
    searched,
    resetting,

    async getQuarters() {
      try {
        loadingQuarters.value = true;
        const { data, error } = await client.GET(
          "/api/v1/cable_ranker/quarters",
        );
        if (error) {
          throw error;
        }
        quartersTaskId.value = data?.task_id;
        if ($ws && data?.task_id) {
          $ws.subscribe({
            channel: "CableRanker",
            payload: {
              id: data.task_id,
            },
          });
        }
      } catch (error: any) {
        loadingQuarters.value = false;
        errorStore.showError(error?.message ?? errorStore.defaultErrorMessage);
        console.error(error);
      }
    },
    async getRankings() {
      loading.value = true;
      rankings.value = [];
      try {
        const { data, error, response } = await client.POST(
          "/api/v1/cable_ranker/quarter_rankings",
          {
            body: {
              params: filters.value,
            } as unknown as schemas["CableRankerGetQuarterRankingsRequest"],
          },
        );
        if (error || !response.ok) {
          throw new Error(
            (error as { message?: string } | undefined)?.message ??
              `Request failed with status ${response.status}`,
          );
        }
        if (!data?.task_id) {
          loading.value = false;
          return;
        }
        rankingsTaskId.value = data.task_id;
        if ($ws) {
          $ws.subscribe({
            channel: "CableRanker",
            payload: {
              id: data.task_id,
            },
          });
        }
      } catch (e: any) {
        loading.value = false;
        errorStore.showError(e?.message ?? "Failed to load rankings");
      }
    },
    setRatingsData(data: Ranking[]) {
      rankings.value = data;
      setAvgRatings();
      setAvgRatingsByQuarter();
      searched.value = true;
      loading.value = false;
      rankingsTaskId.value = undefined;
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCableRankerStore, import.meta.hot));
}
